<template>
  <div class="manager-client-list">
    <!-- Action Buttons -->
    <div class="manager-client-list-actions">
      <button class="manager-client-list-btn manager-client-list-btn-secondary" @click="$emit('refresh')">
        <i class="bi bi-arrow-clockwise"></i>
        Refresh Data
      </button>
      <!-- View Toggle -->
      <ViewToggle v-model="viewMode" />
    </div>

    <!-- Clients Section -->
    <div class="manager-client-list-section">
      <div class="manager-client-list-section-header">
        <h2 class="manager-client-list-section-title">
          <i class="bi bi-people"></i> My Clients
        </h2>
      </div>
      <div v-if="loading" class="manager-client-list-loading">
        <div class="manager-client-list-spinner"></div>
        <p>Loading clients...</p>
      </div>
      <div v-else-if="clients.length === 0" class="manager-client-list-empty">
        <i class="bi bi-people-slash"></i>
        <p>No clients assigned to you.</p>
      </div>
      <!-- Table View -->
      <div v-else-if="viewMode === 'table'" class="manager-client-list-table-container">
        <table class="manager-client-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in tableRows" :key="row.client.id + '-' + (row.isInvited ? 'inv' : 'own')">
              <tr
                @click="handleRowClick(row.client, $event)"
                class="manager-client-list-table-row"
                :class="{
                  'manager-client-list-row--owner-with-invites': !row.isInvited && row.invitedCount > 0,
                  'manager-client-list-row--invited': row.isInvited,
                  'manager-client-list-row--orphan-invited': row.isInvited && row.isOrphan
                }"
              >
                <td>
                  <div
                    class="manager-client-list-name-cell"
                    :class="{ 'manager-client-list-name-cell--invited': row.isInvited }"
                  >
                    <div class="manager-client-list-name-line">
                      <button
                        v-if="!row.isInvited && row.invitedCount > 0"
                        type="button"
                        class="manager-client-list-expand-btn"
                        @click.stop="toggleOwnerExpand(row.client.id)"
                        :title="isOwnerExpanded(row.client.id)
                          ? `Hide ${row.invitedCount} invited user${row.invitedCount === 1 ? '' : 's'}`
                          : `Show ${row.invitedCount} invited user${row.invitedCount === 1 ? '' : 's'}`"
                      >
                        <i
                          class="bi"
                          :class="isOwnerExpanded(row.client.id) ? 'bi-chevron-down' : 'bi-chevron-right'"
                        ></i>
                      </button>
                      <span v-else-if="row.isInvited" class="manager-client-list-invited-connector" aria-hidden="true">
                        <i class="bi bi-arrow-return-right"></i>
                      </span>
                      <span v-else class="manager-client-list-expand-spacer" aria-hidden="true"></span>
                      <span class="manager-client-list-name-text">{{ row.client.firstName }} {{ row.client.lastName }}</span>
                    </div>
                    <div v-if="row.isInvited" class="manager-client-list-user-type-row">
                      <span
                        class="manager-client-list-user-type-badge manager-client-list-user-type-badge--invited"
                        :title="invitedTooltip(row.client)"
                      >
                        <i class="bi bi-person-check"></i> Invited User
                      </span>
                      <span
                        v-if="row.client.invitedRoleLabel"
                        class="manager-client-list-role-badge"
                        :title="`Role granted at invite${row.client.invitedCompanyName ? ' for ' + row.client.invitedCompanyName : ''}`"
                      >
                        {{ row.client.invitedRoleLabel }}
                      </span>
                      <span
                        v-if="row.isOrphan && row.ownerLabel"
                        class="manager-client-list-owner-hint"
                        :title="`Invited into ${row.ownerLabel}'s company`"
                      >
                        <i class="bi bi-link-45deg"></i> under {{ row.ownerLabel }}
                      </span>
                    </div>
                    <div v-else class="manager-client-list-user-type-row">
                      <span
                        class="manager-client-list-user-type-badge manager-client-list-user-type-badge--owner"
                        title="This client owns the company (self-signed up)"
                      >
                        <i class="bi bi-person-badge"></i> Owner
                      </span>
                      <span
                        v-if="row.invitedCount > 0"
                        class="manager-client-list-invited-count-badge"
                        :title="`${row.invitedCount} invited user${row.invitedCount === 1 ? '' : 's'} under this client`"
                      >
                        <i class="bi bi-people"></i> {{ row.invitedCount }} invited
                      </span>
                    </div>
                  </div>
                </td>
                <td>{{ row.client.email }}</td>
                <td>{{ row.client.companyName }}</td>
                <td>
                  <span :class="['manager-client-list-status-badge', row.client.is_active ? 'active' : 'inactive']">
                    {{ row.client.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <div class="manager-client-list-actions-group">
                    <button class="manager-client-list-action-icon" @click.stop="viewClientDetails(row.client)" title="View Details">
                      <i class="bi bi-eye"></i>
                    </button>
                    <button class="manager-client-list-action-icon manager-client-list-action-icon-danger" @click.stop="handleToggleStatus(row.client)" :title="row.client.is_active ? 'Deactivate' : 'Activate'">
                      <i :class="row.client.is_active ? 'bi bi-x-circle' : 'bi bi-check-circle'"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Grid View -->
      <div v-else class="manager-client-list-grid-container">
        <div class="manager-client-list-grid">
          <div
            v-for="client in clients"
            :key="client.id"
            class="manager-client-list-grid-card"
            :class="{ 'manager-client-list-grid-card--invited': client.is_invited_user }"
          >
            <div class="manager-client-list-grid-card-header">
              <span :class="['manager-client-list-status-badge', client.is_active ? 'active' : 'inactive']">
                {{ client.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="manager-client-list-grid-card-body">
              <h3 class="manager-client-list-grid-name">{{ client.firstName }} {{ client.lastName }}</h3>
              <div v-if="client.is_invited_user" class="manager-client-list-user-type-row">
                <span
                  class="manager-client-list-user-type-badge manager-client-list-user-type-badge--invited"
                  :title="invitedTooltip(client)"
                >
                  <i class="bi bi-person-check"></i> Invited User
                </span>
                <span
                  v-if="client.invitedRoleLabel"
                  class="manager-client-list-role-badge"
                  :title="`Role granted at invite${client.invitedCompanyName ? ' for ' + client.invitedCompanyName : ''}`"
                >
                  {{ client.invitedRoleLabel }}
                </span>
                <span
                  v-if="ownerLabelFor(client)"
                  class="manager-client-list-owner-hint"
                  :title="`Invited into ${ownerLabelFor(client)}'s company`"
                >
                  <i class="bi bi-link-45deg"></i> under {{ ownerLabelFor(client) }}
                </span>
              </div>
              <div v-else class="manager-client-list-user-type-row">
                <span class="manager-client-list-user-type-badge manager-client-list-user-type-badge--owner" title="This client owns the company (self-signed up)">
                  <i class="bi bi-person-badge"></i> Owner
                </span>
                <span
                  v-if="invitedCountFor(client.id) > 0"
                  class="manager-client-list-invited-count-badge"
                  :title="`${invitedCountFor(client.id)} invited user${invitedCountFor(client.id) === 1 ? '' : 's'} under this client`"
                >
                  <i class="bi bi-people"></i> {{ invitedCountFor(client.id) }} invited
                </span>
              </div>
              <p class="manager-client-list-grid-email">
                <i class="bi bi-envelope"></i>
                <span>{{ client.email }}</span>
              </p>
              <p class="manager-client-list-grid-company">
                <i class="bi bi-building"></i>
                <span>{{ client.companyName }}</span>
              </p>
            </div>
            <div class="manager-client-list-grid-card-actions">
              <button class="manager-client-list-action-icon" @click.stop="viewClientDetails(client)" title="View Details">
                <i class="bi bi-eye"></i>
              </button>
              <button class="manager-client-list-action-icon manager-client-list-action-icon-danger" @click.stop="handleToggleStatus(client)" :title="client.is_active ? 'Deactivate' : 'Activate'">
                <i :class="client.is_active ? 'bi bi-x-circle' : 'bi bi-check-circle'"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="modalTitle"
      :message="modalMessage"
      :clientName="clientName"
      :confirmText="confirmButtonText"
      @confirm="confirmToggleStatus"
      @cancel="showConfirmModal = false"
    />
  </div>
</template>

<script>
import '../styles/ManagerClientList.css';
import ViewToggle from '@/components/ViewToggle.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

export default {
  name: 'ManagerClientList',
  components: {
    ViewToggle,
    ConfirmModal
  },
  props: {
    clients: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'view', 'toggle-status'],
  data() {
    return {
      viewMode: 'table',
      showConfirmModal: false,
      pendingClient: null,
      // IDs of owners whose invited-user sub-rows are currently collapsed.
      // Default is expanded; we only track explicit opt-outs so newly fetched
      // owners (after an invite is accepted) stay expanded by default.
      collapsedOwnerIds: [],
    }
  },
  computed: {
    /**
     * Map: ownerClientId -> array of invited-user clients belonging to that
     * owner. Only invited users whose owner is also in the current list are
     * grouped here; the rest fall into `orphanInvitedClients` below.
     */
    invitedByOwnerId() {
      const map = new Map();
      const ownerIdSet = new Set(
        this.clients.filter((c) => !c.is_invited_user).map((c) => c.id)
      );
      for (const client of this.clients) {
        if (!client.is_invited_user) continue;
        const ownerId = client.ownerClientId;
        if (ownerId == null || !ownerIdSet.has(ownerId)) continue;
        if (!map.has(ownerId)) map.set(ownerId, []);
        map.get(ownerId).push(client);
      }
      for (const list of map.values()) {
        list.sort((a, b) => {
          const an = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          const bn = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
          return an.localeCompare(bn);
        });
      }
      return map;
    },
    /**
     * Invited users whose owner client isn't in the current manager list.
     * This happens when the cascade hasn't run yet, or when the owner is
     * managed by someone else but their invited user is on this manager.
     * We still surface them so nothing disappears silently.
     */
    orphanInvitedClients() {
      const ownerIdSet = new Set(
        this.clients.filter((c) => !c.is_invited_user).map((c) => c.id)
      );
      return this.clients.filter((c) =>
        c.is_invited_user && (c.ownerClientId == null || !ownerIdSet.has(c.ownerClientId))
      );
    },
    /**
     * Flattened row list for the table: owner row first, then its invited
     * users (when expanded), followed by orphan invited users at the end.
     */
    tableRows() {
      const rows = [];
      for (const client of this.clients) {
        if (client.is_invited_user) continue; // Rendered under its owner.
        const invited = this.invitedByOwnerId.get(client.id) || [];
        rows.push({
          client,
          isInvited: false,
          isOrphan: false,
          invitedCount: invited.length,
          ownerLabel: null,
        });
        if (invited.length > 0 && this.isOwnerExpanded(client.id)) {
          for (const inv of invited) {
            rows.push({
              client: inv,
              isInvited: true,
              isOrphan: false,
              invitedCount: 0,
              ownerLabel: `${client.firstName || ''} ${client.lastName || ''}`.trim() || null,
            });
          }
        }
      }
      for (const inv of this.orphanInvitedClients) {
        rows.push({
          client: inv,
          isInvited: true,
          isOrphan: true,
          invitedCount: 0,
          ownerLabel: this.ownerLabelFor(inv),
        });
      }
      return rows;
    },
    modalTitle() {
      if (!this.pendingClient) return 'Confirm Action';
      return this.pendingClient.is_active ? 'Deactivate Client' : 'Activate Client';
    },
    modalMessage() {
      if (!this.pendingClient) return '';
      if (this.pendingClient.is_active) {
        return 'Are you sure you want to deactivate this client? The client will not be able to access the system until reactivated.';
      } else {
        return 'Are you sure you want to activate this client? The client will be able to access the system.';
      }
    },
    clientName() {
      if (!this.pendingClient) return '';
      return `${this.pendingClient.firstName} ${this.pendingClient.lastName}`;
    },
    confirmButtonText() {
      if (!this.pendingClient) return 'Confirm';
      return this.pendingClient.is_active ? 'Deactivate' : 'Activate';
    }
  },
  methods: {
    viewClientDetails(client) {
      this.$emit('view', client);
    },
    handleToggleStatus(client) {
      this.pendingClient = client;
      this.showConfirmModal = true;
    },
    confirmToggleStatus() {
      if (this.pendingClient) {
        this.$emit('toggle-status', this.pendingClient);
        this.showConfirmModal = false;
        this.pendingClient = null;
      }
    },
    handleRowClick(client, event) {
      // Buttons (view/toggle) and the expand chevron have their own handlers.
      if (event.target.closest('button') || event.target.closest('input')) {
        return;
      }
      this.viewClientDetails(client);
    },
    invitedTooltip(client) {
      const parts = ['Invited user (not a company owner).'];
      if (client?.invitedRoleLabel) parts.push(`Role: ${client.invitedRoleLabel}.`);
      if (client?.invitedCompanyName) parts.push(`Company: ${client.invitedCompanyName}.`);
      const ownerLabel = this.ownerLabelFor(client);
      if (ownerLabel) parts.push(`Invited by/under: ${ownerLabel}.`);
      return parts.join(' ');
    },
    isOwnerExpanded(ownerId) {
      return !this.collapsedOwnerIds.includes(ownerId);
    },
    toggleOwnerExpand(ownerId) {
      const idx = this.collapsedOwnerIds.indexOf(ownerId);
      if (idx > -1) this.collapsedOwnerIds.splice(idx, 1);
      else this.collapsedOwnerIds.push(ownerId);
    },
    /** "First Last" of the owner this invited client belongs to, falling
     *  back to the email when names are missing. */
    ownerLabelFor(client) {
      if (!client?.is_invited_user) return null;
      const first = client.ownerClientFirstName || '';
      const last = client.ownerClientLastName || '';
      const name = `${first} ${last}`.trim();
      return name || client.ownerClientEmail || null;
    },
    /** Count of invited users present in the current list under the given
     *  owner id (used by the grid view's "X invited" badge). */
    invitedCountFor(ownerId) {
      const list = this.invitedByOwnerId.get(ownerId);
      return list ? list.length : 0;
    }
  }
}
</script>
